import {ReportableError} from '@wix/document-manager-utils'
import type {DropCommitContext, FragmentPointer, Namespace, Pointer, SimpleDalAPI} from '@wix/document-services-types'
import type {
    BaselineBlacklistCheck,
    DocumentDataTypes,
    FilterGetter,
    IndexKey,
    KeyValPredicate,
    Null,
    NullUndefined,
    PostSetOperation,
    PostTransactionOperation,
    Transaction,
    ValidationWhitelistCheck,
    ValidatorScope
} from '../types'
import type {IndexedValues, QueryIndex, ValueToIndexIds} from './queryIndex/queryIndex'
import {createStore, DalJsStore, type DalValue, type DmStore, DalStore} from './store'
import {CreateFragmentArgs, createFragmentDal, FragmentDal, getPointersByNamespaceKeys} from './fragment/fragmentDal'
import type {CoreConfig} from '../documentManagerCore'
import _ from 'lodash'
import type {ValidateValue, BaseLineNestedObject} from './validation/dalValidation'
import {createTagManager, TagManager} from './snapshot/tagManager'
import type {EventEmitter} from 'events'
import {createFragmentStore, FragmentDalJsStore, FragmentStore, type FragmentStoreMap} from './fragment/fragmentStore'
import {createList, SnapshotList, Store} from './snapshot/snapshotList'
import {createDalSchema, DalSchema} from './schema/dalSchema'
import {createSnapshotChain, SnapshotDal} from './snapshot/snapshotDal'
import {isFragmentPointer} from '../utils/pointerUtils'
import {createImmutableProxy} from '@wix/wix-immutable-proxy'

export type SetterSet = (pointer: Pointer, value: any) => void
export type Getter = (pointer: Pointer) => DalValue
export type CustomGetter = (dal: PDAL, rootPointer: Pointer) => DalValue
export type DalValueChangeCallback = (pointer: Pointer, oldValue: DalValue, newValue: DalValue) => void

export type PDAL = Pick<DAL, 'queryFilterGetters' | 'query' | 'get'>

export const TransactionEvents = {
    TRANSACTION_REJECTED: 'TRANSACTION_REJECTED',
    TRANSACTION_BY_OTHER: 'TRANSACTION_BY_OTHER',
    LOCAL_TRANSACTION_APPROVED: 'LOCAL_TRANSACTION_APPROVED'
} as const

export const SNAPSHOT_REMOVED_EVENT = 'SNAPSHOT_REMOVED_DUE_TO_CONFLICT'

export type TransactionEvent = keyof typeof TransactionEvents

export interface OpenTransaction {
    id: string
    store: DmStore
    attempt: number
}

// These error constructors explicitly set the prototype since it's the recommended workaround for a TypeScript issue:
// See https://github.com/Microsoft/TypeScript-wiki/blob/master/Breaking-Changes.md#extending-built-ins-like-error-array-and-map-may-no-longer-work
export class CommitsDisabledError extends ReportableError {
    constructor() {
        super({
            errorType: 'CommitsDisabledError',
            message:
                'commitTransaction was called when commits were disabled. Perhaps disableCommits was called without a subsequent call to enableCommits?'
        })
        Object.setPrototypeOf(this, CommitsDisabledError.prototype)
    }
}

export class TransactionRejectionError extends Error {
    constructor(id: string, reason?: string) {
        const reasonMessage = reason ? `. Reason: ${reason}` : ''
        super(`Transaction was rejected by server, transaction id ${id}${reasonMessage}`)
        Object.setPrototypeOf(this, TransactionRejectionError.prototype)
    }
}

export class SnapshotRemovedError extends Error {
    constructor(id: string) {
        super(`Snapshot was removed by client due to conflict, snapshot correlation id ${id}`)
        Object.setPrototypeOf(this, SnapshotRemovedError.prototype)
    }
}

export const basedOnSignatureNS = 'basedOnSignature'
export const PAGE_FRAGMENT_TYPE = 'Pages'

export interface DAL extends SimpleDalAPI {
    readonly queryFilterGetters: Record<string, FilterGetter>
    readonly tagManager: TagManager
    readonly registrar: {
        registerFilter(name: string, filter: ValueToIndexIds): void
        registerValidator(name: string, validate: ValidateValue, validatorScope: ValidatorScope): void
        registerRebaseValidator(name: string, validate: ValidateValue): void
        registerValidationWhitelistCheck(whitelistCheck: ValidationWhitelistCheck): void
        registerPostTransactionOperation(name: string, operation: PostTransactionOperation): void
        registerTransactionDerivedMetadata(
            name: string,
            operation: PostTransactionOperation,
            fragmentType?: string
        ): void
        registerBaselineBlacklistCheck(check: BaselineBlacklistCheck): void
        registerForChangesCallback(callback: DalValueChangeCallback): void
        unregisterForChangesCallback(callback: DalValueChangeCallback): void
    }

    readonly _store: DmStore
    readonly _queryIndex: QueryIndex
    readonly _tentativeStore: DmStore
    readonly _snapshots: SnapshotList
    readonly _currentTransaction: OpenTransaction
    _getMergedOpenTransactionStoreAsJson?(): DalJsStore
    _getMergedTentativeStoreAsJson?(): DalJsStore
    _getMergedApprovedStoreAsJson?(): DalJsStore
    _getAllFragmentsOpenTransactionStoreAsJson?(): FragmentDalJsStore
    _getAllFragmentsApprovedStoreAsJson?(): FragmentDalJsStore

    _getBaseLineViolations(): BaseLineNestedObject
    _getApprovedStoreAsJson(): DalJsStore
    _getTentativeStoreAsJson(): DalJsStore
    _getCommittedStoreAsJson(): DalJsStore
    _getMergedStoreAsJson(): DalJsStore
    _getFragmentsMergedStoreAsJson?(): FragmentDalJsStore
    _getAllTags(): Record<string, string[]>

    approve(pid: string): void
    reject(pid: string): void

    commitTransaction(committer?: string, skipValidations?: boolean): SnapshotDal
    safeCommitTransactionWithErrorReport(additionalErrorTags: Record<string, any>, committer?: string): SnapshotDal
    find(namespace: string, indexKey: IndexKey, predicate: KeyValPredicate, fragmentType?: string): any
    getIndexed(indexKey: IndexKey, fragmentType?: string): IndexedValues
    getIndexKeys(indexName: string, fragmentType?: string): string[]
    get(pointer: Pointer): DalValue
    getRegisteredTypes(): DocumentDataTypes
    getWithPath(pointer: Pointer, innerPath: string | string[]): any
    has(pointer: Pointer): boolean
    isDirty(pointer: Pointer): boolean
    query(
        namespace: string,
        indexKey: IndexKey,
        optionalPredicate?: KeyValPredicate,
        fragmentType?: string
    ): Record<string, DalValue>
    queryKeys(namespace: string, indexKey: IndexKey, optionalPredicate?: KeyValPredicate, fragmentType?: string): any
    getIndexPointers(indexKey: IndexKey, namespace?: string, fragmentType?: string): Pointer[]
    rebase(change: Store, position: string, label?: string): SnapshotDal
    rebaseForeignChange(change: Store, position: string, correlationId?: string, isUndoable?: boolean): SnapshotDal
    remove(pointer: Pointer): void
    set(pointer: Pointer, value: DalValue): void
    setIfChanged(pointer: Pointer, value: DalValue): void
    modify<A, B = A>(pointer: Pointer, f: (a: A) => B): void
    touch(pointer: Pointer): void
    getLastSnapshot(): SnapshotDal | null
    takeSnapshot(tag: string): number
    takeLastApprovedSnapshot(tag: string): void
    validate(tags?: Record<string, any>, fragmentType?: string): void
    validatePendingCommit(tags?: Record<string, any>): void
    createValidationBaseline(): void
    recreateValidationBaseline(): void
    sign(
        rootPointer: Pointer,
        value: DalValue,
        signOver?: Null<SnapshotDal>
    ): {value: DalValue; basedOnSignature: NullUndefined<string>}
    getBasedOnSignatureId(namespace?: string, id?: string): string
    getBasedOnSignaturePointer(pointer: Pointer): Pointer
    getBasedOnSignature(pointer: Pointer): NullUndefined<string>
    setBasedOnSignature(pointer: Pointer, signature: NullUndefined<string>): void
    getTentativeAndAcceptedAsTransaction(): Transaction
    getCurrentOpenTransaction(): Transaction
    mergeToApprovedStore(changes: Store, id?: string): SnapshotDal
    getLastApprovedSnapshot(): SnapshotDal
    dropUncommittedTransaction(reason?: string, e?: any, context?: DropCommitContext): void
    enableCommits(): void
    disableCommits(): void
    withoutCustomGetters(func: Function): any
    readonly schema: DalSchema
    hasSignature(pointer: Pointer): boolean
    getAllItemsInNamespace(namespace: Namespace): Pointer[]
    getItemsInNamespace(fragmentType: string, namespace: Namespace): FragmentPointer[]
    getNamespacesInFragment(fragmentType: string): Namespace[]
    enableShouldNotifyAboutReads(): void
}

export interface CreateArgs {
    coreConfig: CoreConfig
    postSetOperations: PostSetOperation[]
    dmTypes: DocumentDataTypes
    customGetters: Record<string, CustomGetter>
    eventEmitter: EventEmitter
    initialStore?: DalStore | FragmentStoreMap
}

class CannotApproveError extends ReportableError {
    constructor(tx: SnapshotDal | null, id: string, args: any, txCompareIds: SnapshotDal | null) {
        super({
            errorType: 'cannotApproveError',
            message: `Cannot approve, ${id} is not a pending transaction`,
            extras: {
                id,
                ...args,
                unlinked: !tx,
                foundByCompareIds: !!txCompareIds,
                content: [...(tx?.getAllFragmentsStore()?.entries() ?? [])].map(([key, store]) => ({
                    [key]: _.keys(store.asJson())
                }))
            }
        })
        Object.setPrototypeOf(this, CannotApproveError.prototype)
    }
}

class Registrar {
    pagesDal: FragmentDal
    constructor(private dalManager: DocumentManagerDAL, private fragmentDALs: FragmentDALs) {
        this.pagesDal = this.fragmentDALs[PAGE_FRAGMENT_TYPE]
    }

    registerFilter(name: string, filter: ValueToIndexIds): void {
        Object.values(_.omit(this.fragmentDALs, PAGE_FRAGMENT_TYPE)).forEach(fragmentDal => {
            fragmentDal._queryIndex.createFilterFactory(name, filter)
        })
        const filterFactory = this.pagesDal._queryIndex.createFilterFactory(name, filter)
        this.dalManager.queryFilterGetters[filterFactory.indexName] = filterFactory.getFilter
    }

    registerValidator(name: string, validateValue: ValidateValue, validatorScope: ValidatorScope): void {
        _.forEach(this.fragmentDALs, (fragmentDal: FragmentDal, fragmentDalName: string) => {
            if (validatorScope.exclude?.includes(fragmentDalName)) {
                return
            }
            if (validatorScope.include.includes('*') || validatorScope.include.includes(fragmentDalName)) {
                fragmentDal.validator.registerValidator(name, validateValue)
            }
        })
    }

    registerRebaseValidator(name: string, validateValue: ValidateValue): void {
        this.pagesDal.rebaseValidator.registerValidator(name, validateValue)
    }

    registerValidationWhitelistCheck(whitelistCheck: ValidationWhitelistCheck) {
        this.pagesDal.validator.registerWhitelistCheck(whitelistCheck)
    }

    registerPostTransactionOperation(name: string, operation: PostTransactionOperation): void {
        Object.values(this.fragmentDALs).forEach(fragmentDal => {
            fragmentDal.postTransactionOperations[name] = operation
        })
    }

    registerTransactionDerivedMetadata(name: string, operation: PostTransactionOperation, fragmentType?: string): void {
        if (fragmentType) {
            const fragmentDal = this.fragmentDALs[fragmentType]
            if (!fragmentDal) {
                throw new Error(`Fragment type ${fragmentType} is not supported`)
            }
            fragmentDal.transactionDerivedMetadataOperations[name] = operation
        } else {
            Object.values(this.fragmentDALs).forEach(fragmentDal => {
                fragmentDal.transactionDerivedMetadataOperations[name] = operation
            })
        }
    }

    registerBaselineBlacklistCheck(check: BaselineBlacklistCheck): void {
        this.pagesDal.validator.registerBaselineBlacklistCheck(check)
    }

    registerForChangesCallback(callback: DalValueChangeCallback) {
        this.pagesDal.updateCallbacks.push(callback)
    }

    unregisterForChangesCallback(callback: DalValueChangeCallback) {
        _.remove(this.pagesDal.updateCallbacks, e => e === callback)
    }
}

interface FragmentDALs {
    [key: string]: FragmentDal
}

class DocumentManagerDAL implements DAL {
    queryFilterGetters: Record<string, FilterGetter> = {}
    tagManager = createTagManager()
    registrar: Registrar
    readonly schema: DalSchema
    rebaseValidator: any

    private fragmentDALs: FragmentDALs = {}
    private basedOnSignatureMap: Record<string, string> = {}
    private snapshots: SnapshotList
    private lastApproved: Null<SnapshotDal> = null
    private customGettersEnabled: boolean = true
    private commitsEnabled: boolean = true

    constructor(
        private coreConfig: CoreConfig,
        private postSetOperations: PostSetOperation[],
        private dmTypes: DocumentDataTypes,
        private customGetters: Record<string, CustomGetter>,
        private eventEmitter: EventEmitter,
        initialStore?: FragmentStoreMap
    ) {
        this.snapshots = createList()
        this.schema = createDalSchema({
            schemaService: this.coreConfig.schemaService,
            experiments: {}
        })

        this.rebaseValidator = null

        const fragmentTypes = coreConfig.supportedFragments
            ? _.keys(coreConfig.supportedFragments).concat(PAGE_FRAGMENT_TYPE)
            : [PAGE_FRAGMENT_TYPE]
        fragmentTypes.forEach(fragmentType => {
            this.fragmentDALs[fragmentType] = createFragmentDal(this.getFragmentArgs(fragmentType))
        })
        this.registrar = new Registrar(this, this.fragmentDALs)

        if (initialStore) {
            this.mergeToApprovedStore(createFragmentStore(initialStore))
        }
    }

    private getFragmentArgs(fragmentType: string): CreateFragmentArgs {
        return {
            fragmentType,
            coreConfig: this.coreConfig,
            queryFilterGetters: this.queryFilterGetters,
            postSetOperations: this.postSetOperations,
            dmTypes: this.dmTypes,
            customGetters: this.customGetters,
            eventEmitter: this.eventEmitter,
            lastApproved: this.lastApproved,
            snapshots: this.snapshots,
            tagManager: this.tagManager,
            customGettersEnabled: this.customGettersEnabled
        }
    }

    private updateLastApprovedSnapshot(snapshot: SnapshotDal) {
        this.lastApproved = snapshot
        this.applyToAllDALs(dal => dal.updateLastApprovedSnapshot(snapshot))
    }

    private applyToAllDALs<T>(operation: (dal: FragmentDal) => any): Map<string, T> {
        const resultMap = new Map<string, any>()

        _(this.fragmentDALs)
            .toPairs()
            .forEach(([fragmentType, fragmentDal]) => {
                const fragmentResult = operation(fragmentDal)
                if (!_.isNil(fragmentResult)) {
                    resultMap.set(fragmentType, fragmentResult)
                }
            })

        return resultMap
    }

    private applyFragmentChangesOnDALs<T>(
        fragmentStore: FragmentStore,
        operation: (dal: FragmentDal, store: DmStore) => any
    ): Map<string, T> {
        const resultMap = new Map<string, any>()

        fragmentStore.forEach((store, fragmentType) => {
            const dal = fragmentType === PAGE_FRAGMENT_TYPE ? this.pagesDal : this.getFragmentDal(fragmentType)
            resultMap.set(fragmentType, operation(dal, store))
        })

        return resultMap
    }

    private validateFragmentInstance(pointer: Pointer, value: any) {
        const {fragmentInstance} = pointer
        const metaDataFragmentInstance = value?.metaData?.fragmentInstance
        if (fragmentInstance && metaDataFragmentInstance && fragmentInstance !== metaDataFragmentInstance) {
            throw new Error(
                `Fragment instance ${pointer.fragmentInstance} sent in pointer doesn't match fragment instance in metaData ${value.metaData.fragmentInstance}`
            )
        }
    }

    getMergedStoreAsJson(includeCurrentTransaction: boolean): DalJsStore {
        const mergedStore = this.pagesDal.getMergedStore(includeCurrentTransaction) as DmStore
        return mergedStore.asJson()
    }

    enableShouldNotifyAboutReads(): void {
        this.applyToAllDALs(dal => dal.enableShouldNotifyAboutReads())
    }
    /**
     * Merge all the changes since the last approved snapshot up to the new approved
     * snapshot into the main store and update the approved snapshot pointer.
     * Rebuild the tentative store with the remaining unapproved changes.
     * @param id id of the approved snapshot
     */
    approve(id: string) {
        const tx = this.snapshots.findByIdLimit(id, this.lastApproved!)
        if (!tx) {
            throw this.createCannotApproveError(id)
        }

        tx.getAllFragmentsStore().forEach((_store, fragmentType) => {
            if (fragmentType === PAGE_FRAGMENT_TYPE) {
                this.pagesDal.approve(tx)
            } else {
                this.getFragmentDal(fragmentType).approve(tx)
            }
        })

        this.updateLastApprovedSnapshot(tx)
    }

    /**
     * Delete all the tentative snapshots from the snapshot with the given id to the last
     * approved snapshot and rebuild the tentative store with the remaining tentative snapshots
     * @param id - id of the rejected snapshot
     */
    reject(id: string) {
        const tx = this.snapshots.findByIdLimit(id, this.lastApproved!)
        if (!tx) {
            // Possibly sent and later identified as conflict, not necessarily a bug
            return
        }

        this.applyOnSnapshotsRange(this.lastApproved!, tx, (snapshot: SnapshotDal) => {
            this.snapshots.remove(snapshot)
            this.tagManager.remove(snapshot)
        })

        this.applyToAllDALs(dal => dal.removeConflictsAndReport(TransactionEvents.TRANSACTION_REJECTED))
    }

    /**
     * Add the current open transaction to the snapshot chain and merge it to the tentative store.
     * Notify listeners of the commit
     * @param committer
     * @param skipValidations
     */
    commitTransaction(committer?: string | undefined, skipValidations?: boolean | undefined): SnapshotDal {
        if (!this.commitsEnabled) {
            this.coreConfig.logger.captureError(new CommitsDisabledError())
            return this.snapshots.last()!
        }

        const allDALsHaveNothingToCommit = _.every(
            Array.from(this.applyToAllDALs<boolean>(dal => dal.hasNothingToCommit()).values())
        )
        if (allDALsHaveNothingToCommit) {
            return this.snapshots.last()!
        }

        const changes: FragmentStoreMap = this.applyToAllDALs<DmStore>(dal =>
            dal.commitTransactionForFragment(committer, skipValidations)
        )
        if (!changes.size) {
            return this.snapshots.last()!
        }

        const changesStore = createFragmentStore(changes)
        const snapshot = this.snapshots.add(changesStore)

        this.applyFragmentChangesOnDALs<void>(changesStore, (dal, store) =>
            dal.report(store, TransactionEvents.LOCAL_TRANSACTION_APPROVED)
        )

        return snapshot
    }

    safeCommitTransactionWithErrorReport(
        additionalErrorTags: Record<string, any>,
        committer?: string | undefined
    ): SnapshotDal {
        try {
            return this.commitTransaction(committer)
        } catch (error: any) {
            const reportableError = new ReportableError(error)
            reportableError.tags ??= {}
            _.assign(reportableError.tags, additionalErrorTags)
            this.coreConfig.logger.captureError(reportableError)
            return this.commitTransaction(committer, true)
        }
    }

    find(namespace: string, indexKey: IndexKey, predicate: KeyValPredicate, fragmentType: string = PAGE_FRAGMENT_TYPE) {
        return this.getFragmentDal(fragmentType).find(namespace, indexKey, predicate)
    }

    getIndexed(indexKey: IndexKey, fragmentType: string = PAGE_FRAGMENT_TYPE): IndexedValues {
        return this.getFragmentDal(fragmentType).getIndexed(indexKey)
    }

    getIndexKeys(indexName: string, fragmentType: string = PAGE_FRAGMENT_TYPE): string[] {
        return this.getFragmentDal(fragmentType).getIndexKeys(indexName)
    }

    private getFragmentDal(fragmentType: string): FragmentDal {
        const fragmentDal = this.fragmentDALs[fragmentType]
        if (!fragmentDal) {
            throw new Error(`Fragment type ${fragmentType} is not supported`)
        }
        return fragmentDal
    }

    get(pointer: Pointer) {
        if (isFragmentPointer(pointer)) {
            const value = this.getFragmentDal(pointer.fragmentType).get(pointer)
            this.validateFragmentInstance(pointer, value)
            return value
        }
        return this.pagesDal.get(pointer)
    }

    getWithPath(pointer: Pointer, innerPath: string | string[]) {
        if (isFragmentPointer(pointer)) {
            return this.getFragmentDal(pointer.fragmentType).getWithPath(pointer, innerPath)
        }
        return this.pagesDal.getWithPath(pointer, innerPath)
    }

    has(pointer: Pointer): boolean {
        if (isFragmentPointer(pointer)) {
            return this.getFragmentDal(pointer.fragmentType).has(pointer)
        }
        return this.pagesDal.has(pointer)
    }

    isDirty(pointer: Pointer): boolean {
        if (isFragmentPointer(pointer)) {
            return this.getFragmentDal(pointer.fragmentType).isDirty(pointer)
        }
        return this.pagesDal.isDirty(pointer)
    }

    query(
        namespace: string,
        indexKey: IndexKey,
        optionalPredicate?: KeyValPredicate | undefined,
        fragmentType: string = PAGE_FRAGMENT_TYPE
    ): Record<string, any> {
        return this.getFragmentDal(fragmentType).query(namespace, indexKey, optionalPredicate)
    }

    queryKeys(
        namespace: string,
        indexKey: IndexKey,
        optionalPredicate?: KeyValPredicate | undefined,
        fragmentType: string = PAGE_FRAGMENT_TYPE
    ) {
        return this.getFragmentDal(fragmentType).queryKeys(namespace, indexKey, optionalPredicate)
    }

    /**
     * Returns all the pointers in the index referred to by the specified index key and optionally only in the given namespace
     * @param indexKey
     * @param namespace
     * @param fragmentType
     * @returns An array of the pointers in the specified index
     */
    getIndexPointers(
        indexKey: IndexKey,
        namespace?: string | undefined,
        fragmentType: string = PAGE_FRAGMENT_TYPE
    ): Pointer[] {
        return this.getFragmentDal(fragmentType).getIndexPointers(indexKey, namespace, fragmentType)
    }

    rebase(change: Store, position: string, label?: string) {
        const fragmentStore = this.wrapStoreToFragmentStore(change)
        return this._rebase(
            fragmentStore,
            position,
            TransactionEvents.LOCAL_TRANSACTION_APPROVED,
            this.snapshots.generateId(label)
        )
    }

    rebaseForeignChange(change: Store, position: string, correlationId?: string) {
        const fragmentStore = this.wrapStoreToFragmentStore(change)
        const id = correlationId ?? this.snapshots.generateId('unknown-foreign')
        const newSnapshot = this._rebase(fragmentStore, position, TransactionEvents.TRANSACTION_BY_OTHER, id)
        newSnapshot.setAsForeign()
        return newSnapshot
    }

    private wrapStoreToFragmentStore(store: Store): FragmentStore {
        if ('getFragmentStoreMap' in store) {
            return store as FragmentStore
        }
        const fragmentStore = createFragmentStore()
        fragmentStore.setFragmentStore(PAGE_FRAGMENT_TYPE, store as DmStore)
        return fragmentStore
    }

    /**
     * Insert the given snapshot into the snapshot chain at the specified position
     *
     * @param change
     * @param position
     * @param event
     * @param id
     */
    _rebase(change: FragmentStore, position: string, event: TransactionEvent, id: string) {
        const tx = this.snapshots.findByIdLimitInclusive(position, this.lastApproved!)
        if (!tx) {
            return this.rebaseApprovedHistory(change, position, event, id)
        }
        const newSnapshot = this.snapshots.insert(change, position, id)
        this.applyFragmentChangesOnDALs(change, (dal, store) =>
            dal.removeConflictsReportAndVerify(store, position, event, newSnapshot.id)
        )
        return newSnapshot
    }

    remove(pointer: Pointer): void {
        if (isFragmentPointer(pointer)) {
            this.getFragmentDal(pointer.fragmentType).remove(pointer)
        } else {
            this.pagesDal.remove(pointer)
        }
    }

    set(pointer: Pointer, value: any): void {
        if (isFragmentPointer(pointer)) {
            this.validateFragmentInstance(pointer, value)
            this.getFragmentDal(pointer.fragmentType).set(pointer, value)
            this.addBasedOnSignatureToMap(pointer)
        } else {
            this.pagesDal.set(pointer, value)
        }
    }

    addBasedOnSignatureToMap(pointer: Pointer): void {
        const {id, type, fragmentType} = pointer
        if (!fragmentType || !this.hasSignature(pointer)) {
            return
        }
        this.basedOnSignatureMap[this.getBasedOnSignatureId(type, id)] = fragmentType
    }

    setIfChanged(pointer: Pointer, value: any): void {
        if (isFragmentPointer(pointer)) {
            this.getFragmentDal(pointer.fragmentType).setIfChanged(pointer, value)
        } else {
            this.pagesDal.setIfChanged(pointer, value)
        }
    }

    modify<A, B = A>(pointer: Pointer, f: (a: A) => B): void {
        return this.set(pointer, f(this.get(pointer)))
    }

    touch(pointer: Pointer): void {
        if (isFragmentPointer(pointer)) {
            return this.getFragmentDal(pointer.fragmentType).touch(pointer)
        }
        return this.pagesDal.touch(pointer)
    }

    getLastSnapshot(): SnapshotDal | null {
        return this.snapshots.last()
    }

    takeSnapshot(tag: string): number {
        const snapshot = this.commitTransaction('takeSnapshot')
        return this.tagManager.addSnapshot(tag, snapshot)
    }

    takeLastApprovedSnapshot(tag: string): void {
        this.tagManager.addSnapshot(tag, this.getLastApprovedSnapshot())
    }

    validate(tags?: Record<string, any>, fragmentType?: string) {
        if (fragmentType) {
            const dal = this.fragmentDALs[fragmentType]
            dal.validate(tags)
            return
        }
        this.applyToAllDALs(dal => dal.validate(tags))
    }

    validatePendingCommit(tags?: Record<string, any> | undefined): void {
        this.applyToAllDALs(dal => dal.validatePendingCommit(tags))
    }

    createValidationBaseline() {
        this.pagesDal.createValidationBaseline()
    }

    recreateValidationBaseline() {
        this.applyToAllDALs(dal => dal.recreateValidationBaseline())
    }

    sign(
        rootPointer: Pointer,
        value: any,
        signOver?: Null<SnapshotDal> | undefined
    ): {value: any; basedOnSignature: NullUndefined<string>} {
        if (isFragmentPointer(rootPointer)) {
            return this.getFragmentDal(rootPointer.fragmentType).sign(rootPointer, value, signOver)
        }
        return this.pagesDal.sign(rootPointer, value, signOver)
    }

    getBasedOnSignatureId(namespace?: string | undefined, id?: string | undefined): string {
        return `${namespace}$${id}`
    }

    getBasedOnSignaturePointer(pointer: Pointer): Pointer {
        if (isFragmentPointer(pointer)) {
            return this.getFragmentDal(pointer.fragmentType).getBasedOnSignaturePointer(pointer)
        }
        return this.pagesDal.getBasedOnSignaturePointer(pointer)
    }

    getBasedOnSignature(pointer: Pointer): NullUndefined<string> {
        if (isFragmentPointer(pointer)) {
            return this.getFragmentDal(pointer.fragmentType).getBasedOnSignature(pointer)
        }
        return this.pagesDal.getBasedOnSignature(pointer)
    }

    setBasedOnSignature(pointer: Pointer, basedOnSignature: NullUndefined<string>): void {
        if (isFragmentPointer(pointer)) {
            this.getFragmentDal(pointer.fragmentType).setBasedOnSignature(pointer, basedOnSignature)
        } else {
            this.pagesDal.setBasedOnSignature(pointer, basedOnSignature)
        }
    }

    getTentativeAndAcceptedAsTransaction(): Transaction {
        const tentativeAndAccepted = this.pagesDal.getMergedStore(false) as DmStore
        const transaction = {
            id: 'tentativeAndAccepted',
            items: tentativeAndAccepted.getValues()
        }
        return createImmutableProxy(transaction)
    }

    getCurrentOpenTransaction() {
        return this.pagesDal.getCurrentOpenTransaction()
    }

    getLastApprovedSnapshot(): SnapshotDal {
        if (!this.lastApproved) {
            throw new Error('Empty DAL does not have a last approved snapshot')
        }
        return this.lastApproved
    }

    /**
     * Copies changes into the dal store. Called during DS initialization
     * @param changes - the store to copy into the dal store
     * @param label
     */
    mergeToApprovedStore(changes: FragmentStore, label?: string): SnapshotDal {
        const snapshotId = this.snapshots.generateId(label)
        const fragmentStore = changes.clone()
        this.applyFragmentChangesOnDALs(fragmentStore, (dal, store) =>
            dal.validateAndMergeToApprovedStore(store, snapshotId)
        )

        const newApprovedSnapshot = this.lastApproved
            ? this.snapshots.insert(fragmentStore, this.lastApproved.id, snapshotId)
            : this.snapshots.add(fragmentStore, snapshotId)

        this.updateLastApprovedSnapshot(newApprovedSnapshot)
        return newApprovedSnapshot
    }

    dropUncommittedTransaction(reason?: string, e?: any, context: DropCommitContext = {}) {
        this.applyToAllDALs(dal => dal.dropUncommittedTransaction(reason, e, context))
    }

    enableCommits(): void {
        this.commitsEnabled = true
    }

    disableCommits(): void {
        this.commitsEnabled = false
    }

    withoutCustomGetters(func: Function) {
        return (...args: any[]) => {
            try {
                this.customGettersEnabled = false
                this.applyToAllDALs(dal => {
                    ;(dal as any).customGettersEnabled = false
                })
                return func(...args)
            } finally {
                this.customGettersEnabled = true
                this.applyToAllDALs(dal => {
                    ;(dal as any).customGettersEnabled = true
                })
            }
        }
    }

    hasSignature(pointer: Pointer): boolean {
        if (isFragmentPointer(pointer)) {
            return this.getFragmentDal(pointer.fragmentType).hasSignature(pointer)
        }
        return this.pagesDal.hasSignature(pointer)
    }

    getRegisteredTypes(): DocumentDataTypes {
        return this.dmTypes
    }

    getAllItemsInNamespace(namespace: Namespace): Pointer[] {
        const allFragmentNamespaceObj = this.applyToAllDALs<Record<string, any>>(dal =>
            dal.getMergedNamespace(namespace)
        )
        const mergedNamespaceObj = Object.assign({}, ...allFragmentNamespaceObj.values())
        return getPointersByNamespaceKeys(Object.keys(mergedNamespaceObj), namespace)
    }

    getItemsInNamespace(fragmentType: string, namespace: Namespace): FragmentPointer[] {
        return this.getFragmentDal(fragmentType).getAllItemsInNamespace(namespace) as FragmentPointer[]
    }

    getNamespacesInFragment(fragmentType: string): Namespace[] {
        return this.getFragmentDal(fragmentType).getNamespaces()
    }

    private checkForConflictingChanges(changes: FragmentStore): boolean {
        const conflictResults = this.applyFragmentChangesOnDALs<boolean>(changes, (dal, store) =>
            dal.isConflictingWithApprovedValue(store)
        )

        return _.some(Array.from(conflictResults.values()))
    }

    private applyOnSnapshotsRange(from: SnapshotDal, to: SnapshotDal, fn: _.ListIterator<SnapshotDal, any>) {
        const snapshotsToApply = createSnapshotChain(from, to, 'applyOnSnapshotsRange')
        _(snapshotsToApply).reverse().forEach(fn)
    }

    private createSnapshotTrace() {
        const snapshotChain = _(createSnapshotChain(null, this.snapshots.last(), 'createSnapshotTrace'))
            .slice(-30)
            .map('id')
            .value()
        return {
            lastApproved: this.lastApproved?.id,
            snapshotChain,
            currentTags: this.tagManager._getAllTags()
        }
    }

    private createCannotApproveError(id: string) {
        const tx = this.snapshots.findById(id)
        const txCompareIds = this.snapshots.findByIdLimitCompareIds(id, this.lastApproved!)
        return new CannotApproveError(tx, id, this.createSnapshotTrace(), txCompareIds)
    }

    private createCannotRebaseError(position: string, id: string | undefined) {
        return new ReportableError({
            errorType: 'cannotRebaseError',
            message: `Cannot rebase, ${position} does not come before pending transaction`,
            extras: {
                position,
                id
            }
        })
    }

    private createRebaseUnlinkedError(position: string, id: string | undefined) {
        return new ReportableError({
            errorType: 'rebaseUnlinkedError',
            message: `Cannot rebase, ${position} is unlinked`,
            extras: {
                position,
                id,
                ...this.createSnapshotTrace()
            }
        })
    }

    private createRemovedApprovedError(position: string, id: string | undefined) {
        return new ReportableError({
            errorType: 'removedApprovedSnapshotError',
            message: 'We just removed the approved snapshot due to a conflict',
            extras: {
                position,
                id,
                ...this.createSnapshotTrace()
            }
        })
    }

    private rebaseApprovedHistory(change: FragmentStore, position: string, event: TransactionEvent, id?: string) {
        const tx = this.snapshots.findById(position)
        if (!tx) {
            throw this.createRebaseUnlinkedError(position, id)
        }

        if (this.checkForConflictingChanges(change)) {
            throw this.createRemovedApprovedError(position, id)
        }

        this.coreConfig.logger.captureError(this.createCannotRebaseError(position, id))

        const approvedLater: FragmentStore = _.reduce(
            createSnapshotChain(tx, this.lastApproved, 'rebaseApprovedHistory'),
            (mergedStore: FragmentStore, snapshotInRange: SnapshotDal) => {
                snapshotInRange.getAllFragmentsStore().forEach((store, fragmentType) => {
                    if (!mergedStore.hasFragmentStore(fragmentType)) {
                        mergedStore.setFragmentStore(fragmentType, createStore())
                    }
                    mergedStore.getFragmentStore(fragmentType)!.merge(store)
                })
                return mergedStore
            },
            createFragmentStore()
        )

        const relevantChanges: FragmentStore = createFragmentStore()
        change.forEach((changes: DmStore, fragmentType: string) => {
            changes.forEach((pointer: any, value: any) => {
                if (!approvedLater.getFragmentStore(fragmentType)?.has(pointer)) {
                    if (!relevantChanges.hasFragmentStore(fragmentType)) {
                        relevantChanges.setFragmentStore(fragmentType, createStore())
                    }
                    relevantChanges.getFragmentStore(fragmentType)!.set(pointer, value)
                }
            })
        })

        this.applyFragmentChangesOnDALs(relevantChanges, (dal, store) => dal.mergeToApprovedStore(store))

        const newSnapshot = this.snapshots.insert(relevantChanges, position, id)
        this.applyFragmentChangesOnDALs(relevantChanges, (dal, store) =>
            dal.removeConflictsReportAndVerify(store, position, event, newSnapshot.id)
        )
        return newSnapshot
    }

    getMergedStore(includeCurrentTransaction: boolean): FragmentStore {
        return createFragmentStore(this.applyToAllDALs<DmStore>(dal => dal.getMergedStore(includeCurrentTransaction)))
    }

    _getFragmentsMergedStoreAsJson(): FragmentDalJsStore {
        const mergedStore = this.getMergedStore(false)
        return mergedStore.asJson()
    }

    get _store() {
        return this.pagesDal._store
    }

    get _tentativeStore() {
        return this.pagesDal._tentativeStore
    }

    get _snapshots() {
        return this.snapshots
    }

    _getApprovedStoreAsJson() {
        return this.pagesDal._getApprovedStoreAsJson()
    }

    _getTentativeStoreAsJson() {
        return this.pagesDal._getTentativeStoreAsJson()
    }

    _getCommittedStoreAsJson() {
        return this.pagesDal._getCommittedStoreAsJson()
    }

    _getMergedStoreAsJson() {
        return this.pagesDal._getMergedStoreAsJson()
    }

    /** @private */
    get _queryIndex() {
        return this.pagesDal._queryIndex
    }

    /** @private */
    get _currentTransaction() {
        return this.pagesDal._currentTransaction
    }

    _getBaseLineViolations() {
        return this.pagesDal.validator.getBaseLineViolations()
    }

    /** @private */
    _getMergedOpenTransactionStoreAsJson(): DalJsStore {
        const mergedStore = this.pagesDal._currentTransaction.store.asJson()
        Object.values(this.fragmentDALs).forEach(fragmentDal => {
            _.merge(mergedStore, fragmentDal._currentTransaction.store.asJson())
        })
        return mergedStore
    }

    /** @private */
    _getMergedTentativeStoreAsJson(): DalJsStore {
        const mergedStore = this.pagesDal._getTentativeStoreAsJson()
        Object.values(this.fragmentDALs).forEach(fragmentDal => {
            _.merge(mergedStore, fragmentDal._getTentativeStoreAsJson())
        })
        return mergedStore
    }

    /** @private */
    _getMergedApprovedStoreAsJson(): DalJsStore {
        const mergedStore = this.pagesDal._getApprovedStoreAsJson()
        Object.values(this.fragmentDALs).forEach(fragmentDal => {
            _.merge(mergedStore, fragmentDal._getApprovedStoreAsJson())
        })
        return mergedStore
    }

    /** @private */
    private get pagesDal() {
        return this.fragmentDALs[PAGE_FRAGMENT_TYPE]
    }

    /** @private */
    _getAllFragmentsOpenTransactionStoreAsJson(): FragmentDalJsStore {
        const mergedStore: FragmentDalJsStore = {}
        Object.entries(this.fragmentDALs).forEach(([fragmentType, fragmentDal]) => {
            mergedStore[fragmentType] = fragmentDal._currentTransaction.store.asJson()
        })
        return mergedStore
    }

    /** @private */
    _getAllFragmentsApprovedStoreAsJson(): FragmentDalJsStore {
        const approvedStore: FragmentDalJsStore = {}
        Object.entries(this.fragmentDALs).forEach(([fragmentType, fragmentDal]) => {
            approvedStore[fragmentType] = fragmentDal._getApprovedStoreAsJson()
        })
        return approvedStore
    }

    _getAllTags() {
        return this.tagManager._getAllTags()
    }
}

export const createDal = ({
    coreConfig,
    postSetOperations,
    dmTypes,
    customGetters,
    eventEmitter,
    initialStore
}: CreateArgs): DAL => {
    return new DocumentManagerDAL(
        coreConfig,
        postSetOperations,
        dmTypes,
        customGetters,
        eventEmitter,
        initialStore as FragmentStoreMap
    )
}
